import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminAllocateSchoolSubscription,
  adminGetCorporateUsage,
  adminListCorporateSchools,
  adminListCorporateSubscriptionProducts,
  adminListCorporateTeachersBySchool,
  adminUpsertCorporateSchool,
  adminUpsertCorporateTeacher,
} from "./adminApi";
import "./AdminDashboard.css";

const SCHOOL_STATUS_OPTIONS = ["active", "inactive"];
const ROLE_OPTIONS = ["teacher", "admin"];

function displayValue(value) {
  return value === 0 || value === false || value ? String(value) : "-";
}

function remainingCredits(cap, used) {
  return (cap ?? 0) - (used ?? 0);
}

function getSchoolsPayload(response) {
  const rows =
    response?.data?.schools ||
    response?.data?.school_rows ||
    response?.schools ||
    response?.school_rows ||
    null;

  if (Array.isArray(rows)) return rows;

  const ids = response?.school_ids || response?.data?.school_ids || [];
  if (Array.isArray(ids)) {
    return ids.map((school_id) => ({ school_id }));
  }

  return [];
}

function getTeachersPayload(response) {
  return response?.data?.teachers || response?.teachers || response?.data || [];
}

function getUsagePayload(response) {
  return response?.data || response || {};
}

function getSubscriptionProductsPayload(response) {
  const products =
    response?.data?.products ||
    response?.data?.subscription_products ||
    response?.products ||
    response?.subscription_products ||
    [];

  return Array.isArray(products) ? products : [];
}

function getProductPlanId(product) {
  return product?.plan_id || product?.product_id || product?.id || "";
}

function getProductLabel(product) {
  const planId = getProductPlanId(product);
  const name =
    product?.plan_name ||
    product?.product_name ||
    product?.name ||
    product?.subscription_name ||
    "";
  return name && planId ? `${name} (${planId})` : name || planId;
}

function UsageVerification({ usage }) {
  const active = usage?.active_usage || null;

  if (!active) {
    return <div className="admin-meta">No active subscription row found.</div>;
  }

  return (
    <div className="admin-summary-grid">
      <div className="admin-summary-item"><b>subscription_id:</b> {displayValue(active.subscription_id)}</div>
      <div className="admin-summary-item"><b>plan_id:</b> {displayValue(active.plan_id)}</div>
      <div className="admin-summary-item"><b>status:</b> {displayValue(active.status)}</div>
      <div className="admin-summary-item"><b>subscription_start_date:</b> {displayValue(active.subscription_start_date)}</div>
      <div className="admin-summary-item"><b>subscription_end_date:</b> {displayValue(active.subscription_end_date)}</div>
      <div className="admin-summary-item"><b>compo:</b> {displayValue(active.compo_credit_used)} / {displayValue(active.compo_credit_cap)} / {remainingCredits(active.compo_credit_cap, active.compo_credit_used)}</div>
      <div className="admin-summary-item"><b>oral:</b> {displayValue(active.oral_credit_used)} / {displayValue(active.oral_credit_cap)} / {remainingCredits(active.oral_credit_cap, active.oral_credit_used)}</div>
    </div>
  );
}

export default function AdminCorporateSchoolsPage() {
  const navigate = useNavigate();
  const [currentSchoolId, setCurrentSchoolId] = useState("");
  const [schoolId, setSchoolId] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [schoolAdminEmail, setSchoolAdminEmail] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [schoolStatus, setSchoolStatus] = useState("active");
  const [schools, setSchools] = useState([]);
  const [teacherEmail, setTeacherEmail] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [teacherDisplayName, setTeacherDisplayName] = useState("");
  const [teacherRole, setTeacherRole] = useState("teacher");
  const [teacherIsActive, setTeacherIsActive] = useState("true");
  const [teachers, setTeachers] = useState([]);
  const [planId, setPlanId] = useState("");
  const [subscriptionProducts, setSubscriptionProducts] = useState([]);
  const [loadingSubscriptionProducts, setLoadingSubscriptionProducts] =
    useState(false);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [subscriptionStartDate, setSubscriptionStartDate] = useState("");
  const [durationMonths, setDurationMonths] = useState("");
  const [usageData, setUsageData] = useState(null);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  function handleUnauthorized(err) {
    if (err?.status === 401) {
      navigate("/admin/login", { replace: true });
      return true;
    }
    return false;
  }

  async function loadSubscriptionProducts() {
    setLoadingSubscriptionProducts(true);

    try {
      const response = await adminListCorporateSubscriptionProducts();
      setSubscriptionProducts(getSubscriptionProductsPayload(response));
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setSubscriptionProducts([]);
      setError(err.message || "Failed to load subscription products.");
    } finally {
      setLoadingSubscriptionProducts(false);
    }
  }

  useEffect(() => {
    loadSubscriptionProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function selectSchool(school) {
    const selectedId = school?.school_id || "";
    setCurrentSchoolId(selectedId);
    setSchoolId(selectedId);
    setSchoolName(school?.school_name || "");
    setSchoolAdminEmail(school?.school_admin_email || "");
    setBillingEmail(school?.billing_email || "");
    setSchoolStatus(
      SCHOOL_STATUS_OPTIONS.includes(school?.status) ? school.status : "active"
    );
    setTeacherEmail("");
    setTeacherId("");
    setTeacherDisplayName("");
    setTeacherRole("teacher");
    setTeacherIsActive("true");
    setUsageData(null);
    if (selectedId) {
      loadTeachers(selectedId);
      loadUsage(selectedId, false);
    }
  }

  async function loadSchools() {
    setLoadingSchools(true);
    setError("");
    setSuccess("");

    try {
      const response = await adminListCorporateSchools();
      setSchools(getSchoolsPayload(response));
      setSuccess("Schools loaded.");
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to load schools.");
      setSchools([]);
    } finally {
      setLoadingSchools(false);
    }
  }

  async function saveSchool() {
    const payload = {
      school_id: schoolId.trim(),
      school_name: schoolName.trim(),
      school_admin_email: schoolAdminEmail.trim().toLowerCase(),
      billing_email: billingEmail.trim().toLowerCase(),
      status: schoolStatus,
    };

    if (!payload.school_id) {
      setError("school_id is required.");
      return;
    }
    if (!payload.school_admin_email) {
      setError("school_admin_email is required.");
      return;
    }
    if (!SCHOOL_STATUS_OPTIONS.includes(payload.status)) {
      setError("School status must be active or inactive.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminUpsertCorporateSchool(payload);
      setCurrentSchoolId(payload.school_id);
      setSuccess("School saved.");
      await loadSchools();
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to save school.");
    } finally {
      setActionLoading(false);
    }
  }

  async function loadTeachers(targetSchoolId = currentSchoolId || schoolId) {
    const trimmedSchoolId = targetSchoolId.trim();

    if (!trimmedSchoolId) {
      setError("Select or enter a school_id first.");
      return;
    }

    setLoadingTeachers(true);
    setError("");

    try {
      const response = await adminListCorporateTeachersBySchool(trimmedSchoolId);
      const rows = getTeachersPayload(response);
      setTeachers(Array.isArray(rows) ? rows : []);
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to load teachers.");
      setTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  }

  async function saveTeacher() {
    const selectedSchoolId = (currentSchoolId || schoolId).trim();
    const payload = {
      school_id: selectedSchoolId,
      teacher_email: teacherEmail.trim().toLowerCase(),
      teacher_id: teacherId.trim(),
      display_name: teacherDisplayName.trim(),
      role: teacherRole,
      is_active: teacherIsActive === "true",
    };

    if (!payload.school_id) {
      setError("Select or save a school first.");
      return;
    }
    if (!payload.teacher_email) {
      setError("teacher_email is required.");
      return;
    }
    if (!payload.teacher_id) {
      setError("teacher_id is required.");
      return;
    }
    if (!ROLE_OPTIONS.includes(payload.role)) {
      setError("Teacher role must be teacher or admin.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminUpsertCorporateTeacher(payload);
      setSuccess("Teacher saved.");
      await loadTeachers(payload.school_id);
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to save teacher.");
    } finally {
      setActionLoading(false);
    }
  }

  async function loadUsage(targetSchoolId = currentSchoolId || schoolId, showSuccess = true) {
    const trimmedSchoolId = targetSchoolId.trim();

    if (!trimmedSchoolId) {
      setError("Select or enter a school_id first.");
      return;
    }

    try {
      const response = await adminGetCorporateUsage(trimmedSchoolId);
      setUsageData(getUsagePayload(response));
      if (showSuccess) {
        setSuccess("Usage loaded.");
      }
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to load usage.");
      setUsageData(null);
    }
  }

  async function allocateSubscription() {
    const selectedSchoolId = (currentSchoolId || schoolId).trim();
    const payload = {
      school_id: selectedSchoolId,
      plan_id: planId.trim(),
      subscription_id: subscriptionId.trim(),
      subscription_start_date: subscriptionStartDate,
    };

    if (durationMonths.trim()) {
      payload.duration_months = Number(durationMonths);
    }

    if (!payload.school_id) {
      setError("Select or save a school first.");
      return;
    }
    if (!payload.plan_id) {
      setError("plan_id is required before allocation.");
      return;
    }
    if (!payload.subscription_id) {
      setError("subscription_id is required before allocation.");
      return;
    }
    if (
      durationMonths.trim() &&
      (!Number.isInteger(payload.duration_months) ||
        payload.duration_months <= 0)
    ) {
      setError("duration_months must be a positive integer if provided.");
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccess("");

    try {
      await adminAllocateSchoolSubscription(payload);
      setSuccess("Subscription allocated.");
      await loadUsage(payload.school_id);
    } catch (err) {
      if (handleUnauthorized(err)) return;
      setError(err.message || "Failed to allocate subscription.");
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="admin-content">
      <div className="admin-card">
        <h2 className="admin-section-title">Corporate Schools - Onboarding Process</h2>
        <div className="admin-meta" style={{ lineHeight: 1.6, marginBottom: 12 }}>
          Use this page to manually set up a corporate school from start to
          finish. First create or select the school profile, then add the
          teachers who should be allowed to log in, then allocate a subscription
          so the school receives writing and oral credits. Finish by checking
          Usage Verification to confirm the active subscription and credit
          balances were created correctly.
        </div>
        <div className="admin-summary-grid" style={{ marginBottom: 12 }}>
          <div className="admin-summary-item"><b>School Profile:</b> enter a stable school_id, school name, admin email, optional billing email, and active/inactive status.</div>
          <div className="admin-summary-item"><b>School List:</b> load existing schools and select one to fill the form and set the current school.</div>
          <div className="admin-summary-item"><b>Add Teacher:</b> add each teacher email and teacher_id under the selected school; role controls teacher/admin access.</div>
          <div className="admin-summary-item"><b>Allocate Subscription:</b> choose an active plan and enter a manual subscription_id, for example manual_starter_school_2026_01.</div>
        </div>
        {error && <div className="admin-error" style={{ marginBottom: 12 }}>{error}</div>}
        {success && <div className="admin-success" style={{ marginBottom: 12 }}>{success}</div>}
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">School Profile</h3>
        <div className="admin-form-grid">
          <div className="admin-form-row">
            <label>school_id</label>
            <input className="admin-input" value={schoolId} onChange={(e) => setSchoolId(e.target.value)} placeholder="school_id" />
          </div>
          <div className="admin-form-row">
            <label>school_name</label>
            <input className="admin-input" value={schoolName} onChange={(e) => setSchoolName(e.target.value)} placeholder="School name" />
          </div>
          <div className="admin-form-row">
            <label>school_admin_email</label>
            <input className="admin-input" value={schoolAdminEmail} onChange={(e) => setSchoolAdminEmail(e.target.value)} placeholder="admin@example.com" />
          </div>
          <div className="admin-form-row">
            <label>billing_email optional</label>
            <input className="admin-input" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} placeholder="billing@example.com" />
          </div>
          <div className="admin-form-row">
            <label>status</label>
            <select className="admin-input" value={schoolStatus} onChange={(e) => setSchoolStatus(e.target.value)}>
              {SCHOOL_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="admin-button-row">
          <button type="button" className="admin-button admin-button-primary" onClick={saveSchool} disabled={actionLoading}>
            Save School
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">School List</h3>
        <div className="admin-button-row">
          <button type="button" className="admin-button admin-button-primary" onClick={loadSchools} disabled={loadingSchools}>
            {loadingSchools ? "Loading..." : "Load Schools"}
          </button>
        </div>
        {schools.length ? (
          <div style={{ overflowX: "auto", marginTop: 16 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr>
                  {["school_id", "school_name", "school_admin_email", "billing_email", "status", "updated_at", ""].map((heading) => (
                    <th key={heading} style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: 8 }}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schools.map((school) => (
                  <tr key={school.school_id}>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(school.school_id)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(school.school_name)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(school.school_admin_email)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(school.billing_email)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(school.status)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(school.updated_at)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>
                      <button type="button" className="admin-button admin-button-secondary" onClick={() => selectSchool(school)}>
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-meta" style={{ marginTop: 16 }}>No schools loaded.</div>
        )}
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Add Teacher</h3>
        <div className="admin-meta" style={{ marginBottom: 12 }}>
          Selected school: {currentSchoolId || schoolId || "-"}
        </div>
        <div className="admin-form-grid">
          <div className="admin-form-row">
            <label>teacher_email</label>
            <input className="admin-input" value={teacherEmail} onChange={(e) => setTeacherEmail(e.target.value)} placeholder="teacher@example.com" />
          </div>
          <div className="admin-form-row">
            <label>teacher_id</label>
            <input className="admin-input" value={teacherId} onChange={(e) => setTeacherId(e.target.value)} placeholder="teacher_id" />
          </div>
          <div className="admin-form-row">
            <label>display_name</label>
            <input className="admin-input" value={teacherDisplayName} onChange={(e) => setTeacherDisplayName(e.target.value)} placeholder="Teacher name" />
          </div>
          <div className="admin-form-row">
            <label>role</label>
            <select className="admin-input" value={teacherRole} onChange={(e) => setTeacherRole(e.target.value)}>
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
          <div className="admin-form-row">
            <label>is_active</label>
            <select className="admin-input" value={teacherIsActive} onChange={(e) => setTeacherIsActive(e.target.value)}>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          </div>
        </div>
        <div className="admin-button-row">
          <button type="button" className="admin-button admin-button-primary" onClick={saveTeacher} disabled={actionLoading}>
            Add / Update Teacher
          </button>
          <button type="button" className="admin-button admin-button-secondary" onClick={() => loadTeachers()} disabled={loadingTeachers}>
            {loadingTeachers ? "Loading..." : "Reload Teachers"}
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Teachers for Selected School</h3>
        {teachers.length ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr>
                  {["teacher_email", "teacher_id", "display_name", "role", "is_active", "updated_at"].map((heading) => (
                    <th key={heading} style={{ textAlign: "left", borderBottom: "1px solid #e5e7eb", padding: 8 }}>{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {teachers.map((teacher) => (
                  <tr key={`${teacher.teacher_email}-${teacher.teacher_id}`}>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.teacher_email)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.teacher_id)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.display_name)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.role)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.is_active)}</td>
                    <td style={{ borderBottom: "1px solid #f3f4f6", padding: 8 }}>{displayValue(teacher.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-meta">No teachers loaded for the selected school.</div>
        )}
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Allocate Subscription Manually</h3>
        <div className="admin-meta" style={{ marginBottom: 12 }}>
          Example subscription_id: manual_starter_{currentSchoolId || schoolId || "school_id"}_2026_01
        </div>
        <div className="admin-form-grid">
          <div className="admin-form-row">
            <label>school_id</label>
            <input className="admin-input" value={currentSchoolId || schoolId || ""} readOnly />
          </div>
          <div className="admin-form-row">
            <label>plan_id</label>
            <select
              className="admin-input"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              disabled={loadingSubscriptionProducts}
            >
              <option value="">
                {loadingSubscriptionProducts
                  ? "Loading plans..."
                  : "Select active plan"}
              </option>
              {subscriptionProducts.map((product) => {
                const productPlanId = getProductPlanId(product);
                if (!productPlanId) return null;
                return (
                  <option key={productPlanId} value={productPlanId}>
                    {getProductLabel(product)}
                  </option>
                );
              })}
            </select>
            {!loadingSubscriptionProducts && !subscriptionProducts.length ? (
              <div className="admin-meta" style={{ marginTop: 6 }}>
                No active subscription products loaded.
              </div>
            ) : null}
          </div>
          <div className="admin-form-row">
            <label>subscription_id</label>
            <input className="admin-input" value={subscriptionId} onChange={(e) => setSubscriptionId(e.target.value)} placeholder="manual_starter_school_2026_01" />
          </div>
          <div className="admin-form-row">
            <label>subscription_start_date</label>
            <input className="admin-input" type="date" value={subscriptionStartDate} onChange={(e) => setSubscriptionStartDate(e.target.value)} />
          </div>
          <div className="admin-form-row">
            <label>duration_months optional</label>
            <input className="admin-input" value={durationMonths} onChange={(e) => setDurationMonths(e.target.value)} placeholder="12" />
          </div>
        </div>
        <div className="admin-button-row">
          <button type="button" className="admin-button admin-button-primary" onClick={allocateSubscription} disabled={actionLoading}>
            Allocate Subscription
          </button>
          <button type="button" className="admin-button admin-button-secondary" onClick={() => loadUsage()} disabled={actionLoading}>
            Refresh Usage
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3 className="admin-section-title">Usage Verification</h3>
        <UsageVerification usage={usageData} />
      </div>
    </div>
  );
}
